import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Workflow, Calendar, Globe, Users, Phone, Upload } from 'lucide-react';

interface IntegrationCard {
  name: string;
  description: string;
  icon: React.ElementType;
}

const INTEGRATIONS: IntegrationCard[] = [
  {
    name: 'Calendly',
    description: 'Schedule meetings and appointments',
    icon: Calendar,
  },
  {
    name: 'TripAdvisor',
    description: 'Manage tour and activity bookings',
    icon: Globe,
  },
  {
    name: 'HubSpot',
    description: 'CRM integration for lead tracking',
    icon: Users,
  },
  {
    name: 'CallRail',
    description: 'Phone call tracking and attribution',
    icon: Phone,
  },
];

export default function BookingIntegrations() {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Booking Integrations</h1>
        <p className="text-muted-foreground">
          Connect your booking systems to automatically track service bookings and leads.
        </p>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {INTEGRATIONS.map((integration) => {
          const Icon = integration.icon;
          return (
            <Card key={integration.name}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base">{integration.name}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription>{integration.description}</CardDescription>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">Not connected</Badge>
                  <Button size="sm" variant="outline" disabled>
                    Coming soon
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Separator />

      {/* Manual / Direct Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <Upload className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Manual / Direct</CardTitle>
              <CardDescription>
                Add bookings manually via CSV upload or API.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant="outline" size="sm" disabled>
              Upload CSV
            </Button>
            <Button variant="outline" size="sm" disabled>
              API Documentation
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Manual import capabilities will be available once the booking data model is finalized.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
